import type { UseFetchOptions, UseFetchReturn } from "@vueuse/core";
import { computed, type MaybeRef, toValue } from "vue";

import type { DeepPartial } from "@/types/globalTypes";
import type { SavedSearchItem } from "@/types/savedSearch.ts";
import { savedSearchApiClient } from "@/utils/apiClients.ts";

function useSaveSearch(
  savedSearchItem: MaybeRef<DeepPartial<SavedSearchItem>>,
  options: UseFetchOptions = {
    immediate: false,
  },
): UseFetchReturn<SavedSearchItem> {
  return savedSearchApiClient("", {
    ...options,
    async beforeFetch({ url, options }) {
      const item = toValue(savedSearchItem);
      if (!item.name) {
        throw new Error("No name provided");
      }
      if (!item.color) {
        throw new Error("no color provided");
      }
      if (!item.filterRules) {
        throw new Error("no search Params to be saved");
      }
      const trimmedUrl = url.replace(/\/$/, "");
      return {
        url: trimmedUrl,
        options,
      };
    },
  })
    .post(() => {
      const item = toValue(savedSearchItem);
      return {
        rank: item?.rank ?? 0,
        hidden: item?.hidden ?? false,
        name: item.name,
        color: item.color,
        filterRules: item.filterRules,
        notificationSettings: item.notificationSettings,
      };
    })
    .json();
}

function useRenameSearch(
  savedSearchId: MaybeRef<string>,
  nameAndColor: MaybeRef<Partial<SavedSearchItem>>,
  options: UseFetchOptions = {
    immediate: false,
  },
): UseFetchReturn<SavedSearchItem> {
  const url = computed(() => `${toValue(savedSearchId)}`);
  return savedSearchApiClient(url, {
    ...options,
    beforeFetch: () => {
      if (!toValue(nameAndColor).name) {
        throw new Error("No name provided");
      }
      if (!toValue(nameAndColor).color) {
        throw new Error("no color provided");
      }
    },
  })
    .patch(() => ({ ...toValue(nameAndColor) }))
    .json();
}

function useEditNotificationSearch(
  savedSearchId: MaybeRef<string>,
  notificationSettings: MaybeRef<DeepPartial<SavedSearchItem>>,
  options: UseFetchOptions = {
    immediate: false,
  },
): UseFetchReturn<SavedSearchItem> {
  const url = computed(() => `/${toValue(savedSearchId)}`);
  return savedSearchApiClient(url, options)
    .patch(() => ({ ...toValue(notificationSettings) }))
    .json();
}

function useAllSavedSearches(
  options: UseFetchOptions = {
    immediate: false,
  },
): UseFetchReturn<SavedSearchItem[]> {
  return savedSearchApiClient("", {
    ...options,
    async beforeFetch({ url, options }) {
      const trimmedUrl = url.replace(/\/$/, "");
      return {
        url: trimmedUrl,
        options,
      };
    },
  })
    .get()
    .json();
}

function useDeleteSavedSearch(
  savedSearchId: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<void> {
  const url = computed(() => `${toValue(savedSearchId)}`);
  return savedSearchApiClient<void>(url, {
    ...options,
    afterFetch(ctx) {
      ctx.data = null;
      return ctx;
    },
  }).delete();
}

export {
  useAllSavedSearches,
  useDeleteSavedSearch,
  useEditNotificationSearch,
  useRenameSearch,
  useSaveSearch,
};
